import type { Direction } from './popout-types';

const MIN_EDGE_DISTANCE = 4;

export interface PopoutLayoutBox {
  anchorWidth: number;
  anchorHeight: number;
  anchorLeft: number;
  anchorTop: number;
  contentWidth: number;
  contentHeight: number;
}

export interface PopoutLayoutOffsets {
  direction: Direction;
  alignment: 'left' | 'center' | 'right';
  offsetTop: number;
  offsetLeft: number;
}

export interface PopoutLayoutResult {
  nextX: number;
  nextY: number;
  effectiveDirection: Direction;
}

function computeVerticalPlacement(args: {
  direction: Direction;
  anchorTopY: number;
  anchorHeight: number;
  contentHeight: number;
  offsetTop: number;
  boundingHeight: number;
}): Pick<PopoutLayoutResult, 'nextY' | 'effectiveDirection'> {
  const {
    direction,
    anchorTopY,
    anchorHeight,
    contentHeight,
    offsetTop,
    boundingHeight
  } = args;

  const yDownward = anchorTopY + anchorHeight + offsetTop;
  const yUpward = anchorTopY - contentHeight - offsetTop;

  switch (direction) {
    default:
      throw new Error('Unknown direction variant.');

    case 'downward':
      if (
        yDownward + contentHeight > boundingHeight - MIN_EDGE_DISTANCE &&
        yUpward > MIN_EDGE_DISTANCE
      ) {
        return { nextY: yUpward, effectiveDirection: 'upward' };
      }

      return { nextY: yDownward, effectiveDirection: 'downward' };

    case 'upward':
      if (yUpward < MIN_EDGE_DISTANCE) {
        return { nextY: yDownward, effectiveDirection: 'downward' };
      }

      return { nextY: yUpward, effectiveDirection: 'upward' };
  }
}

export function computeViewportFixedLayout(
  box: PopoutLayoutBox,
  options: PopoutLayoutOffsets
): PopoutLayoutResult {
  const {
    anchorWidth,
    anchorHeight,
    anchorLeft,
    anchorTop,
    contentWidth,
    contentHeight
  } = box;

  const { alignment, direction, offsetTop, offsetLeft } = options;

  const boundingWidth = window.innerWidth;
  const boundingHeight = window.innerHeight;

  const anchorCenterX = anchorLeft + anchorWidth / 2;

  let nextX: number;

  switch (alignment) {
    default:
      throw new Error('Unknown alignment variant.');

    case 'left':
      nextX = anchorLeft + offsetLeft;
      break;

    case 'right':
      nextX = anchorLeft + anchorWidth - contentWidth + offsetLeft;
      break;

    case 'center':
      nextX = anchorCenterX - contentWidth / 2 + offsetLeft;
      break;
  }

  if (nextX + contentWidth > boundingWidth - MIN_EDGE_DISTANCE) {
    nextX = boundingWidth - contentWidth - MIN_EDGE_DISTANCE;
  }

  if (nextX < MIN_EDGE_DISTANCE) {
    nextX = MIN_EDGE_DISTANCE;
  }

  const { nextY, effectiveDirection } = computeVerticalPlacement({
    direction,
    anchorTopY: anchorTop,
    anchorHeight,
    contentHeight,
    offsetTop,
    boundingHeight
  });

  return { nextX, nextY, effectiveDirection };
}

export function computeOffsetParentLayout(
  box: PopoutLayoutBox,
  boundaryRect: DOMRectReadOnly,
  options: PopoutLayoutOffsets
): PopoutLayoutResult {
  const {
    anchorWidth,
    anchorHeight,
    anchorLeft,
    anchorTop,
    contentWidth,
    contentHeight
  } = box;

  const { alignment, direction, offsetTop, offsetLeft } = options;

  const anchorLeftX = anchorLeft - boundaryRect.left;
  const anchorTopY = anchorTop - boundaryRect.top;

  const anchorCenterX = anchorLeftX + anchorWidth / 2;

  let nextX: number;

  switch (alignment) {
    default:
      throw new Error('Unknown alignment variant.');

    case 'left':
      nextX = anchorLeftX + offsetLeft;
      break;

    case 'right':
      nextX = anchorLeftX + anchorWidth - contentWidth + offsetLeft;
      break;

    case 'center':
      nextX = anchorCenterX - contentWidth / 2 + offsetLeft;
      break;
  }

  const boundingWidth = boundaryRect.width;
  const boundingHeight = boundaryRect.height;

  if (nextX + contentWidth > boundingWidth - MIN_EDGE_DISTANCE) {
    nextX = boundingWidth - contentWidth - offsetLeft;
  }

  if (nextX < MIN_EDGE_DISTANCE) {
    nextX = offsetLeft;
  }

  const { nextY, effectiveDirection } = computeVerticalPlacement({
    direction,
    anchorTopY,
    anchorHeight,
    contentHeight,
    offsetTop,
    boundingHeight
  });

  return { nextX, nextY, effectiveDirection };
}
